import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import {
  ExpenseCategoryDto,
  ExpensePaymentMethodOptions,
} from '../../services/generalExpensesService';

export interface ExpensesFilterValues {
  categoryId: number | null;
  paymentMethod: number | null;
  dateFrom: string;
  dateTo: string;
}

interface ExpensesFilterBarProps {
  categories: ExpenseCategoryDto[];
  filters: ExpensesFilterValues;
  onChange: (next: ExpensesFilterValues) => void;
  onClear: () => void;
}

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  padding: 16px;
  margin-bottom: 16px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  direction: rtl;
  text-align: right;
  background: var(--surface-color);
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  @media (max-width: 767px) {
    min-width: 0;
    width: 100%;
  }
`;

const DateInput = styled.input`
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  direction: rtl;
  width: 160px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  height: 37px;

  &:hover {
    background: var(--hover-background);
    color: var(--text-primary);
  }
`;

const ExpensesFilterBar: React.FC<ExpensesFilterBarProps> = ({
  categories,
  filters,
  onChange,
  onClear,
}) => {
  const hasActiveFilter =
    filters.categoryId !== null ||
    filters.paymentMethod !== null ||
    !!filters.dateFrom ||
    !!filters.dateTo;

  return (
    <Bar>
      <Field>
        <Label>קטגוריה</Label>
        <Select
          value={filters.categoryId ?? ''}
          onChange={e =>
            onChange({ ...filters, categoryId: e.target.value ? Number(e.target.value) : null })
          }
        >
          <option value="">כל הקטגוריות</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <Label>אופן תשלום</Label>
        <Select
          value={filters.paymentMethod ?? ''}
          onChange={e =>
            onChange({ ...filters, paymentMethod: e.target.value ? Number(e.target.value) : null })
          }
        >
          <option value="">כל אופני התשלום</option>
          {ExpensePaymentMethodOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <Label>מתאריך</Label>
        <DateInput
          type="date"
          value={filters.dateFrom}
          max={filters.dateTo || undefined}
          onChange={e => onChange({ ...filters, dateFrom: e.target.value })}
        />
      </Field>

      <Field>
        <Label>עד תאריך</Label>
        <DateInput
          type="date"
          value={filters.dateTo}
          min={filters.dateFrom || undefined}
          onChange={e => onChange({ ...filters, dateTo: e.target.value })}
        />
      </Field>

      {hasActiveFilter && (
        <ClearButton type="button" onClick={onClear}>
          <X size={16} />
          נקה סינון
        </ClearButton>
      )}
    </Bar>
  );
};

export default ExpensesFilterBar;
